import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../context/SettingsContext';
import client from '../api/client';
import {
    Briefcase, Plus, Archive, Edit2, Trash2, Search, X, 
    MoreVertical, CheckCircle, Clock, Eye, EyeOff, Users,
    FileText, PlusCircle, Download, FilePlus, ChevronRight,
    Target, Zap, Shield, TrendingUp, ChevronLeft
} from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import { toast } from 'react-hot-toast';

const Projects = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const { currency } = useSettings();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDesc, setSortDesc] = useState(true);

    const [formData, setFormData] = useState({
        name: '', status: 'В работе', is_archived: false, client_id: '', 
        product_id: null, custom_products: [] 
    });

    // Queries
    const { data: projects, isLoading } = useQuery({
        queryKey: ['projects', showArchived],
        queryFn: async () => (await client.get('/projects', { params: { include_archived: showArchived } })).data
    });
    const { data: clientsList } = useQuery({ queryKey: ['clients'], queryFn: async () => (await client.get('/crm/leads')).data });
    const { data: productsList } = useQuery({ queryKey: ['products'], queryFn: async () => (await client.get('/products')).data });

    const createMutation = useMutation({
        mutationFn: (data) => client.post('/projects', data),
        onSuccess: () => { queryClient.invalidateQueries(['projects']); setIsModalOpen(false); resetForm(); toast.success('Проект создан'); }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/projects/${id}`, data),
        onSuccess: () => { queryClient.invalidateQueries(['projects']); setIsModalOpen(false); resetForm(); toast.success('Проект обновлен'); }
    });

    const resetForm = () => {
        setFormData({ name: '', status: 'В работе', is_archived: false, client_id: '', product_id: null, custom_products: [] });
        setEditingProject(null);
    };

    const handleEdit = (p) => {
        setEditingProject(p);
        setFormData({ name: p.name, status: p.status, is_archived: p.is_archived, client_id: p.client_id || '', product_id: p.product_id || null, custom_products: p.custom_products || [] });
        setIsModalOpen(true);
    };

    const filteredProjects = projects?.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
        if (!sortKey) return 0;
        const valA = a[sortKey] || 0;
        const valB = b[sortKey] || 0;
        return sortDesc ? valB - valA : valA - valB;
    }) || [];

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8 appearance-none">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">Проектный Офис</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Venture Ecosystem</p>
                </div>
                <div className="flex gap-3 md:gap-4 flex-wrap">
                    <button 
                        onClick={() => setShowArchived(!showArchived)} 
                        className={`flex-1 md:flex-none px-6 md:px-8 py-4 md:py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[9px] md:text-[10px] tracking-widest transition-all italic border ${
                            showArchived ? 'bg-blue-600/10 border-blue-500/50 text-blue-500' : 'bg-neutral-900 border-neutral-800 text-neutral-500 hover:text-white'
                        }`}
                    >
                        {showArchived ? 'Скрыть архив' : 'Показать архив'}
                    </button>
                    <button 
                        onClick={() => { resetForm(); setIsModalOpen(true); }} 
                        className="flex-[1.5] md:flex-none bg-blue-600 md:bg-white text-white md:text-black px-6 md:px-10 py-4 md:py-5 rounded-2xl md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-2xl flex items-center justify-center gap-3 italic"
                    >
                        <Plus size={20}/> {isMobile ? 'Запуск' : 'Запустить проект'}
                    </button>
                </div>
            </header>

            {/* Responsive Filter/Search */}
            <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 rounded-3xl md:rounded-[3.5rem] shadow-2xl mb-8 md:mb-12 flex flex-col lg:flex-row gap-6 md:gap-8 italic">
                <div className="relative flex-1">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                    <input 
                        type="text" 
                        placeholder="Поиск объектов в портфеле..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        className="w-full bg-neutral-950 border border-neutral-800 text-white pl-16 pr-6 py-5 md:py-6 rounded-2xl md:rounded-[2rem] text-sm font-bold outline-none focus:border-blue-500" 
                    />
                </div>
            </div>

            {/* Modular Grid Display */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 min-h-[400px]">
                {isLoading ? (
                    [1,2,3,4,5,6].map(i => (
                        <div key={i} className="h-64 bg-neutral-900 border border-neutral-800 rounded-[2.5rem] md:rounded-[3.5rem] animate-pulse" />
                    ))
                ) : filteredProjects.length === 0 ? (
                    <div className="col-span-full py-20 text-center opacity-30 flex flex-col items-center gap-4">
                        <Archive size={48} />
                        <p className="text-xs font-black uppercase tracking-widest italic">Объекты не найдены</p>
                    </div>
                ) : filteredProjects.map(p => (
                    <div 
                        key={p.id} 
                        onClick={() => handleEdit(p)} 
                        className="bg-neutral-900 border border-neutral-800 p-8 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl relative overflow-hidden group hover:border-blue-500/30 transition-all italic cursor-pointer active:scale-[0.98] md:active:scale-100"
                    >
                        {/* Background Decoration - Hidden on very small screens to reduce noise */}
                        <div className="absolute top-0 right-0 p-10 opacity-5 -mr-10 -mt-10 group-hover:scale-110 transition-transform duration-700 pointer-events-none">
                            <Target size={isMobile ? 80 : 160} />
                        </div>
                        
                        <div className="flex justify-between items-start mb-6 md:mb-8 relative z-10">
                            <div className="p-3 md:p-4 rounded-2xl md:rounded-3xl bg-neutral-950 text-blue-500 border border-neutral-800 shadow-xl">
                                <Briefcase size={isMobile ? 20 : 28}/>
                            </div>
                            <div className={`px-3 md:px-4 py-1.5 md:py-2 rounded-xl text-[8px] md:text-[9px] font-black uppercase tracking-widest border ${
                                p.status === 'Завершен' 
                                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                            }`}>
                                {p.status}
                            </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter mb-2 leading-none relative z-10 group-hover:text-blue-400 transition-colors">
                            {p.name}
                        </h3>
                        <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest mb-8 md:mb-10 relative z-10 truncate">
                            {p.client_name || 'Внутренний проект'}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4 md:gap-8 pt-6 md:pt-8 border-t border-white/5 relative z-10">
                            <div>
                                <p className="text-[9px] md:text-[10px] font-black text-neutral-600 uppercase tracking-widest mb-1 flex items-center gap-2 italic">
                                    <TrendingUp size={12}/> Доход
                                </p>
                                <p className="text-lg md:text-xl font-black text-white italic">
                                    {(p.income || 0).toLocaleString()} {currency}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[9px] md:text-[10px] font-black text-neutral-600 uppercase tracking-widest mb-1 flex items-center justify-end gap-2 italic">
                                    Margin <Shield size={12}/>
                                </p>
                                <p className="text-lg md:text-xl font-black text-emerald-500 italic">
                                    {Math.round(p.margin || 0)}%
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal remains consistent but we ensure it's mobile-ready */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[150] flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-2xl rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-2xl flex flex-col italic relative overflow-hidden">
                        <div className="flex justify-between items-center mb-8 md:mb-10">
                            <h3 className="text-xl md:text-3xl font-black text-white uppercase italic tracking-tighter">
                                {editingProject ? 'Конфигурация проекта' : 'Запуск нового объекта'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 md:w-14 md:h-14 bg-neutral-800 rounded-xl md:rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={isMobile ? 20 : 28}/>
                            </button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); if (editingProject) updateMutation.mutate({ id: editingProject.id, data: formData }); else createMutation.mutate(formData); }} className="space-y-6 md:space-y-8 max-h-[70vh] overflow-y-auto scrollbar-hide pr-2">
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Название объекта</label>
                                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 md:p-6 rounded-2xl md:rounded-[2rem] font-bold outline-none focus:border-blue-500" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Участник</label>
                                    <select value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-2xl md:rounded-[1.5rem] font-bold appearance-none"><option value="">-- Выбрать --</option>{clientsList?.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}</select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Статус</label>
                                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-2xl md:rounded-[1.5rem] font-bold appearance-none"><option value="В работе">В работе</option><option value="Завершен">Завершен</option><option value="Приостановлен">Приостановлен</option></select>
                                </div>
                            </div>
                            <button type="submit" className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-2xl md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest shadow-2xl active:scale-95 transition-all mt-4 italic">
                                Записать изменения
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projects;
